import { Link } from 'react-router-dom';

import { useSession } from '../hooks/useSession';

/**
 * Gate a view behind a signed-in session.
 *
 * THIS IS A CONVENIENCE, NOT A SECURITY BOUNDARY. It decides what to render, not what
 * the customer may read: every protected endpoint authorises independently server-side,
 * scoping each lookup to the session's own customer. Someone who bypassed this component
 * entirely would still get 401s and 404s from the API.
 *
 * Its real job is to avoid firing requests that are certain to fail, and to explain the
 * situation rather than showing an empty page.
 */
export default function RequireSession({ children }: { children: React.ReactNode }) {
  const { state } = useSession();

  if (state.status === 'loading') {
    return (
      <p className="mm-help" role="status">
        Checking your session…
      </p>
    );
  }

  if (state.status === 'anonymous') {
    return (
      <div data-testid="sign-in-required">
        <h1 className="mm-display">Sign in to continue</h1>
        <p className="mm-lede mt-4">
          Your orders are private. Sign in with the email you used when ordering and we&rsquo;ll
          send you a link.
        </p>
        <Link to="/sign-in" className="mm-btn-secondary mt-6 inline-flex">
          Sign in
        </Link>
      </div>
    );
  }

  return <>{children}</>;
}
